import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { Field, useFormikContext } from 'formik';
import {
  GridRowModes,
  GridToolbarContainer,
  useGridApiRef,
  GridActionsCellItem,
  GridToolbarFilterButton,
} from '@mui/x-data-grid-premium';
import DeleteIcon from '@mui/icons-material/Delete';
import { v4 as uuidv4 } from 'uuid';

import {
  ButtonUI,
  DataGridIdentifiers,
  DatePickerUI,
  DialogUI,
  FormErrorUI,
  FormLabelUI,
  InputUI,
  SelectUI,
} from '../Interface';
import warehouseService from '../../service/warehouse.service';
import DataGridPremiumUI from '../Interface/DatagridPremmium/DataGridPremiumUI';
import AddButtonUI from '../Interface/AddButtonUI';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import { BinLevelList, BinLevelMode } from '../../config/warehouse';

const EditToolbar = (props) => {
  const { onAddRow } = props;

  const handleClick = async () => {
    onAddRow();
  };

  return (
    <GridToolbarContainer>
      <AddButtonUI onClick={handleClick} />
      <GridToolbarFilterButton />
    </GridToolbarContainer>
  );
};

const BinDialog = ({ open, onClose, selectedValuesState, updateWhs }) => {
  const apiRef = useGridApiRef();
  const { selectedValues, setSelectedValues } = selectedValuesState;
  const { values, errors, setFieldValue, validateField } = useFormikContext();

  const [rows, setRows] = useState([]);
  const [changedRows, setChangedRows] = useState([]);

  const getData = () => {
    warehouseService.getBins(selectedValues?.code).then((res) => {
      setRows(res);
    });
  };

  useEffect(() => {
    if (!selectedValues?.code) return;
    if (!open) return;
    getData();
  }, [open, selectedValues?.code]);

  const handleDialogClose = () => {
    setRows([]);
    setChangedRows([]);
    onClose();
  };

  const handleDeleteRow = async (binId) => {
    const findedBin = rows.find((r) => r.binId === binId);
    if (!findedBin.isNew) {
      await warehouseService.deleteBin(binId);
    }

    setRows((prev) => prev.filter((r) => r.binId !== binId));
    setChangedRows((prev) => prev.filter((r) => r.binId !== binId));

    addNotification({
      content: 'Sikeres törlés!',
      type: NotificationType.WARNING,
    });
  };

  const handleSave = async () => {
    try {
      const missingCode = changedRows.some((row) => !row.binCode || row.binCode.trim() === '');
      if (missingCode) {
        addNotification({
          content: 'A rakhely kódja kötelező.',
          type: NotificationType.WARNING,
        });
        return;
      }
      if (changedRows.length === 0) {
        await updateWhs(values);
        return;
      }

      const saveData = changedRows.map((r) => ({
        ...r,
        binId: r.isNew ? null : r.binId,
        isNew: undefined,
      }));

      await updateWhs(values);
      const data = await warehouseService.saveBins(saveData);
      //setRefetchTable(true);
      setSelectedValues(data);
      addNotification({
        content: 'Sikeres mentés!',
        type: NotificationType.SUCCESS,
      });
    } catch (error) {
      addNotification({
        content: 'Hiba történt a mentés során!',
        type: NotificationType.ERROR,
      });
    }
  };
  const handleCancel = () => {
    handleDialogClose();
  };

  const handleAddRow = () => {
    const newRow = {
      binId: uuidv4(),
      isNew: true,
      sysBin: false,
      active: true,
      whsCode: selectedValues?.code ?? '',
      binCode: '',
      level1: '',
      level2: '',
      level3: '',
      level4: '',
      level5: '',
    };

    setRows((prev) => [...prev, newRow]);
    setChangedRows((prev) => [...prev, newRow]);
    setTimeout(() => {
      stopOtherRowEdits(newRow.binId);
      apiRef.current.startRowEditMode({ id: newRow.binId });
    }, 0);
  };

  const updateRow = (updatedRow) => {
    setChangedRows((prev) => {
      const otherChanges = prev.filter((r) => r.binId !== updatedRow.binId);
      return [...otherChanges, updatedRow];
    });
    setRows((prev) => {
      const otherRows = prev.filter((r) => r.binId !== updatedRow.binId);
      return [...otherRows, updatedRow];
    });
  };

  const stopOtherRowEdits = (activeId) => {
    const rowIds = apiRef.current.getAllRowIds?.() ?? [];
    rowIds.forEach((rowId) => {
      if (rowId === activeId) return;
      if (apiRef.current.getRowMode(rowId) === GridRowModes.Edit) {
        apiRef.current.stopRowEditMode({ id: rowId });
      }
    });
  };

  const handleFieldChange = async (field, newValue, row) => {
    updateRow({ ...row, [field]: newValue });
  };

  const normalizeAlpha = (value) =>
    String(value ?? '')
      .toUpperCase()
      .replace(/[^A-Z]/g, '');

  const normalizeNumeric = (value) => {
    const cleaned = String(value ?? '').replace(/\D/g, '');
    if (!cleaned) return '';
    const numeric = Math.min(parseInt(cleaned, 10), 99999);
    return Number.isNaN(numeric) ? '' : String(numeric);
  };

  const formatLevelValue = (level, rawValue) => {
    const mode = values?.[`level${level}Mode`];
    if (mode === 'numeric') {
      const normalized = normalizeNumeric(rawValue);
      if (!normalized) return '';
      const padding = Number(values?.[`level${level}Padding`] ?? 0);
      if (padding > 0) return normalized.padStart(padding, '0');
      return normalized;
    }
    return normalizeAlpha(rawValue);
  };

  const computeBinCode = (row) => {
    const separator = values?.binSeparator ?? '';
    const parts = [];
    let hasLevel = false;
    for (let level = 1; level <= levelCount; level += 1) {
      const rawValue = row?.[`level${level}`];
      if (rawValue === null || rawValue === undefined || rawValue === '') break;
      const formatted = formatLevelValue(level, rawValue);
      if (!formatted) break;
      hasLevel = true;
      parts.push(formatted);
    }
    if (!hasLevel) return '';
    if (selectedValues?.code) {
      parts.unshift(String(selectedValues.code));
    }
    return parts.join(separator);
  };

  const levelCount = useMemo(() => {
    const configured = Number(values?.binLevel ?? selectedValues?.binLevel ?? 0);
    return Math.max(0, Math.min(5, configured));
  }, [values?.binLevel, selectedValues?.binLevel]);

  useEffect(() => {
    for (let level = 1; level <= levelCount; level += 1) {
      const modeName = `level${level}Mode`;
      if (!values?.[modeName]) {
        setFieldValue(modeName, 'numeric');
      }
    }
  }, [levelCount, setFieldValue, values]);

  const prevLevelCountRef = useRef(levelCount);

  useEffect(() => {
    const prevLevelCount = prevLevelCountRef.current;
    if (levelCount < prevLevelCount) {
      for (let level = levelCount + 1; level <= 5; level += 1) {
        setFieldValue(`level${level}Mode`, null);
        setFieldValue(`level${level}Padding`, null);
      }
      setRows((prev) =>
        prev.map((row) => {
          const updated = { ...row };
          for (let level = levelCount + 1; level <= 5; level += 1) {
            updated[`level${level}`] = '';
          }
          updated.binCode = computeBinCode(updated);
          return updated;
        })
      );
      setChangedRows((prev) =>
        prev.map((row) => {
          const updated = { ...row };
          for (let level = levelCount + 1; level <= 5; level += 1) {
            updated[`level${level}`] = '';
          }
          updated.binCode = computeBinCode(updated);
          return updated;
        })
      );
    }
    prevLevelCountRef.current = levelCount;
  }, [levelCount, computeBinCode, setFieldValue]);

  const levels = useMemo(() => Array.from({ length: levelCount }, (_v, i) => i + 1), [levelCount]);
  const hasNonSystemBins = useMemo(() => rows.some((row) => !row.sysBin), [rows]);
  const minBinLevel = useMemo(() => {
    if (!hasNonSystemBins) return 1;
    const saved = Number(selectedValues?.binLevel ?? 1);
    return Math.max(1, Math.min(5, saved));
  }, [hasNonSystemBins, selectedValues?.binLevel]);
  const binLevelOptions = useMemo(() => BinLevelList.filter((option) => option.value >= minBinLevel), [minBinLevel]);
  const savedBinLevel = useMemo(() => Number(selectedValues?.binLevel ?? 0), [selectedValues?.binLevel]);

  const columns = () => {
    const levelColumns = levels.map((level) => ({
      field: `level${level}`,
      headerName: `Szint ${level}`,
      width: 140,
      editable: true,
      renderEditCell: (data) => {
        const mode = values?.[`level${level}Mode`];
        const prevValue = level > 1 ? data.row?.[`level${level - 1}`] : 'OK';
        //const disabled = !mode || !prevValue;

        return (
          <InputUI
            type="text"
            fullWidth
            value={data.value ?? ''}
            variant="standard"
            // disabled={disabled}
            onChange={async (e) => {
              const rawValue = e.target.value;
              const sanitized = mode === 'numeric' ? normalizeNumeric(rawValue) : normalizeAlpha(rawValue);
              const updatedRow = {
                ...data.row,
                [data.field]: sanitized,
              };
              updatedRow.binCode = computeBinCode(updatedRow);
              updateRow(updatedRow);
              data.api.setEditCellValue({
                id: data.id,
                field: data.field,
                value: sanitized,
              });
            }}
          />
        );
      },
      renderCell: (data) => (data.value ? String(data.value).toUpperCase() : ''),
    }));

    return [
      {
        field: 'binCode',
        headerName: 'Kód',
        width: 180,
        editable: false,
        //valueGetter: (_data, row) => computeBinCode(row),
      },
      {
        field: 'sysBin',
        headerName: 'Rendszer',
        width: 110,
        editable: false,
        type: 'boolean',
      },
      {
        field: 'active',
        headerName: 'Aktív',
        width: 100,
        editable: true,
        type: 'boolean',
      },
      ...levelColumns,
      {
        field: 'actions',
        type: 'actions',
        headerName: '',
        width: 60,
        getActions: (params) => {
          if (params.row?.sysBin) return [];
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Törlés"
              onClick={() => handleDeleteRow(params.row.binId)}
              showInMenu={false}
            />,
          ];
        },
      },
    ];
  };

  return (
    <DialogUI open={open} onClose={handleDialogClose} headerContent={'Rakhelyek kezelése'} maxWidth="lg" fullWidth>
      <Box sx={{ mt: 2 }}>
        <Grid container spacing={1} mb={2} sx={{ background: '#E9F5FE', borderRadius: '4px', p: '0.5rem' }}>
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'grid',
                gap: 1,
                gridTemplateColumns: 'repeat(5, 210px)',
                justifyContent: 'start',
                overflowX: 'auto',
              }}
            >
              <Box
                sx={{
                  p: 1.5,
                  gridRow: 1,
                  gridColumn: '1 / span 3',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Typography variant="h5" color="text.secondary" fontWeight="bold">
                  {selectedValues?.code} - {selectedValues?.name}
                </Typography>
              </Box>
              <Box
                sx={{
                  background: '#F7FAFC',
                  borderRadius: 1,
                  p: 1,
                  gridRow: 1,
                  gridColumn: '4 / span 1',
                }}
              >
                <Field
                  name="binLevel"
                  component={SelectUI}
                  fullWidth
                  label={<FormLabelUI text="Szintek száma" />}
                  variant="standard"
                  helperText={<FormErrorUI message={errors.binLevel} />}
                  onChange={(e) => {
                    const value = e.target.value;
                    const clampedValue = Math.max(Number(value ?? minBinLevel), minBinLevel);
                    setFieldValue('binLevel', clampedValue ?? null).then(() => {
                      validateField('binLevel');
                    });
                  }}
                  value={values.binLevel ?? ''}
                  option={binLevelOptions}
                />
              </Box>
              <Box
                sx={{
                  background: '#F7FAFC',
                  borderRadius: 1,
                  p: 1,
                  gridRow: 1,
                  gridColumn: '5 / span 1',
                }}
              >
                <Field
                  type="text"
                  name="binSeparator"
                  as={InputUI}
                  fullWidth
                  label={<FormLabelUI text="Elválasztó karakter" />}
                  variant="standard"
                  helperText={<FormErrorUI message={errors.binSeparator} />}
                  InputLabelProps={{ shrink: values.binSeparator && values.binSeparator !== '' }}
                  disabled={hasNonSystemBins}
                />
              </Box>
              {levels.map((level) => {
                const modeName = `level${level}Mode`;
                const paddingName = `level${level}Padding`;
                const modeValue = values?.[modeName] ?? '';
                const paddingDisabled = modeValue !== 'numeric';
                const lockExistingLevel = hasNonSystemBins && level <= savedBinLevel;

                return (
                  <Box
                    key={level}
                    sx={{
                      background: '#F7FAFC',
                      borderRadius: 1,
                      p: 1,
                      gridRow: 2,
                      gridColumn: `${level} / span 1`,
                    }}
                  >
                    <Field
                      name={modeName}
                      component={SelectUI}
                      fullWidth
                      label={<FormLabelUI text={`Szint ${level} mód`} />}
                      variant="standard"
                      helperText={<FormErrorUI message={errors?.[modeName]} />}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFieldValue(modeName, value ?? null).then(() => {
                          validateField(modeName);
                        });
                      }}
                      value={modeValue}
                      option={BinLevelMode}
                      disabled={lockExistingLevel}
                    />
                    <Field
                      type="number"
                      name={paddingName}
                      as={InputUI}
                      fullWidth
                      label={<FormLabelUI text={`Szint ${level} padding`} />}
                      variant="standard"
                      helperText={<FormErrorUI message={errors?.[paddingName]} />}
                      InputLabelProps={{ shrink: values?.[paddingName] !== '' && values?.[paddingName] != null }}
                      disabled={paddingDisabled || lockExistingLevel}
                      onChange={(e) => {
                        const value = e.target.value;
                        const numericValue = value === '' ? null : Number(value);
                        setFieldValue(paddingName, numericValue).then(() => {
                          validateField(paddingName);
                        });
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          </Grid>
        </Grid>

        <DataGridPremiumUI
          apiRef={apiRef}
          datagridIdentifier={DataGridIdentifiers.BinTable}
          height={450}
          columns={columns()}
          rows={rows}
          getRowId={(row) => row.binId}
          isCellEditable={(params) => !params.row?.sysBin}
          editMode="row"
          slots={{
            toolbar: EditToolbar,
          }}
          slotProps={{
            toolbar: {
              selectedValues,
              rows,
              setRows,
              setChangedRows,
              onAddRow: handleAddRow,
            },
          }}
          onCellDoubleClick={(_params, event) => {
            event.defaultMuiPrevented = true;
          }}
          onCellClick={(params, event) => {
            const rowMode = apiRef.current.getRowMode(params.id);
            if (rowMode === GridRowModes.View) {
              stopOtherRowEdits(params.id);
              apiRef.current.startRowEditMode({ id: params.id });
            }
          }}
          headerFilters={false}
          getRowHeight={() => 'auto'}
        />
      </Box>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
        <ButtonUI text="Mentés" className="bg-success" onClick={handleSave} />
        <ButtonUI text="Mégse" color="blue-gray" variant="gradient" onClick={handleCancel} />
      </Box>
    </DialogUI>
  );
};

export default BinDialog;
